import 'swiper/css'
import '@/assets/styles/globals.css'
import '@/assets/styles/tailwind.css'
import '@/assets/styles/fonts.css'
import '@/assets/styles/custom-scrollbar.css'
import '@/app/plugins'
import '@/app/polyfills'

import * as Sentry from '@sentry/nextjs'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useEffect, useState } from 'react'

import { AuthService } from '@/api/models/auth'
import { pageTitle } from '@/app/core'
import { useProjectId } from '@/app/hooks/useProjectId'
import { NextPageWithLayout } from '@/app/types'
import { setAppHeight } from '@/app/utils'
import { RefreshSessionOverlay } from '@/components/features/auth-provider/ui/RefreshSessionOverlay'
import { ReactQueryProvider } from '@/components/features/react-query-provider'
import SomethingWentWrong from '@/components/pages/plugs/SomethingWentWrong'
import { SearchEngineMetaTags } from '@/components/shared/search-engine-meta-tags'
import { handleAxiosErrorEvent } from '@/utils/sentry'

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
  sampleRate: process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod' ? 1 : 0.4,
  beforeSend: handleAxiosErrorEvent
})

export default function CustomApp({
  Component,
  pageProps
}: AppPropsWithLayout) {
  const [initialized, setInitialized] = useState(false)

  if (!initialized) {
    AuthService.applySavedToken()
    setInitialized(true)
  }

  const getLayout = Component.getLayout ?? (page => page)

  useEffect(() => {
    setAppHeight()
    window.addEventListener('resize', setAppHeight)

    return () => {
      window.removeEventListener('resize', setAppHeight)
    }
  }, [])

  useProjectId()

  return (
    <Sentry.ErrorBoundary fallback={<SomethingWentWrong />}>
      <ReactQueryProvider dehydratedState={pageProps.ssrState}>
        <>
          <Head>
            <title>{pageTitle()}</title>
          </Head>
          <SearchEngineMetaTags />

          {getLayout(<Component {...pageProps} />)}
          <RefreshSessionOverlay />
        </>
      </ReactQueryProvider>
    </Sentry.ErrorBoundary>
  )
}
